import React, { useState, useEffect } from "react";
import { Button } from "react-bootstrap";
import { FaTimesCircle } from "react-icons/fa";
import { useDropzone } from "react-dropzone";
import { useLocation, useNavigate } from "react-router-dom";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import firebaseApp from "../fireconnect";
import { ToastContainer, toast } from "react-toastify";
import { uploadToCloudinary } from "../utils/cloudinary";

const AddBlog = () => {
  const [title, setTitle] = useState("");
  const [content, setContent] = useState("");
  const [image, setImage] = useState([]);
  const [blogId, setBlogId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [uploadingImages, setUploadingImages] = useState(false);

  const { getRootProps, getInputProps } = useDropzone({
    onDrop: async (acceptedFiles) => {
      setUploadingImages(true);
      try {
        for (const file of acceptedFiles) {
          const cloudinaryUrl = await uploadToCloudinary(file);
          setImage((prevImages) => [...prevImages, cloudinaryUrl]);
        }
        toast.success("Images uploaded successfully!");
      } catch (error) {
        console.error("Error uploading images:", error);
        toast.error("Failed to upload images. Please try again.");
      } finally {
        setUploadingImages(false);
      }
    },
    multiple: true,
    accept: { 'image/*': [] },
  });

  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    if (location.state && location.state.blogData) {
      const { blogData } = location.state;
      setBlogId(blogData.id);
      setTitle(blogData.title);
      setContent(blogData.content);
      setImage(blogData.image || []);
    }
  }, [location]);

  const addBlogData = () => {
    setLoading(true);
    const formattedDate = new Date().toLocaleDateString();
    const updatedBlog = {
      title,
      content,
      image,
      date: new Date(),
      formattedDate,
    };

    let db = firebaseApp.firestore();

    const action = blogId
      ? db.collection("Blog").doc(blogId).update(updatedBlog)
      : db.collection("Blog").add(updatedBlog);

    action
      .then(() => {
        setLoading(false);
        navigate("/Admin/BlogTable", {
          state: {
            message: blogId
              ? "Blog updated successfully!"
              : "Blog added successfully!",
          },
        });
      })
      .catch((error) => {
        console.error("Error saving blog:", error);
        setLoading(false);
      });

    setTitle("");
    setContent("");
    setImage([]);
  };

  const removeImage = (img) => {
    setImage((prev) => prev.filter((item) => item !== img));
  };

  const toolbarOptions = [
    [{ font: [] }, { header: [1, 2, 3, 4, 5, 6, false] }],
    [{ list: "ordered" }, { list: "bullet" }],
    ["bold", "italic", "underline", "strike"],
    [{ align: [] }],
    ["link", "image"],
    [{ color: [] }, { background: [] }],
    ["blockquote", "code-block"],
    [{ script: "sub" }, { script: "super" }],
    [{ indent: "-1" }, { indent: "+1" }],
    [{ direction: "rtl" }],
    ["clean"],
  ];

  const modules = { toolbar: toolbarOptions };

  return (
    <>
      <h4 className="mt-1">{blogId ? "Edit Blog" : "Add Blog"}</h4>
      <section className="px-2 pt-2">
        <div className="px-1">
          <div className="form-section row AdminTable rounded-2 pt-3 scrollable-container">
            <div className="form-group col-12">
              <label className="fw-bold">Blog Title</label>
              <input
                type="text"
                className="form-control"
                onChange={(e) => setTitle(e.target.value)}
                value={title}
                required
              />
            </div>

            <div className="form-group col-12">
              <label className="fw-bold">Blog Content</label>
              <ReactQuill
                value={content}
                onChange={setContent}
                theme="snow"
                placeholder="Enter blog content"
                modules={modules}
              />
            </div>

            <div className="form-group col-12">
              <label className="fw-bold">Images</label>
              <div
                {...getRootProps({ className: "dropzone" })}
                className="dropzone"
                style={{ opacity: uploadingImages ? 0.6 : 1 }}
              >
                <input {...getInputProps()} />
                {uploadingImages ? (
                  <p>Uploading images to cloud... Please wait</p>
                ) : (
                  <p>Drag & drop images here, or click to select files</p>
                )}
              </div>
              {image.length > 0 && (
                <div className="row mt-3">
                  {image.map((img, index) => (
                    <div key={index} className="col-2 position-relative">
                      <img className="img-fluid w-100" src={img} alt="upload" />
                      <Button
                        onClick={() => removeImage(img)}
                        style={{
                          position: "absolute",
                          top: "5px",
                          right: "5px",
                          backgroundColor: "transparent",
                          border: "none",
                        }}
                      >
                        <FaTimesCircle
                          style={{ color: "#f00", fontSize: "20px" }}
                        />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="form-group">
              <Button
                onClick={addBlogData}
                variant="dark"
                className="submit-btn"
                disabled={loading || uploadingImages}
              >
                {loading
                  ? blogId
                    ? "Updating..."
                    : "Saving..."
                  : blogId
                    ? "Update Blog"
                    : "Add Blog"}
              </Button>
            </div>
          </div>
        </div>
      </section>
      <ToastContainer />
    </>
  );
};

export default AddBlog;
